// Утилиты для работы с графиками
import * as XLSX from 'xlsx';
import { formatDate, parseUserDateInput } from './dateParser';

export { parseUserDateInput } from './dateParser';

/** Подгоняет высоту textarea под текст (data-auto-resize="true") */
export const adjustAutoResizeTextareas = () => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('textarea[data-auto-resize="true"]').forEach((el) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  });
};

/**
 * Нормализация даты в YYYY-MM-DD (для API и хранения).
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    return parseUserDateInput(date) || '';
  }
  if (date instanceof Date && !isNaN(date.getTime())) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return '';
};

/**
 * Форматирование даты для отображения в формате DD/MM/YYYY
 */
export const formatDateDisplay = (date) => {
  return formatDate(date);
};

/**
 * Расчёт длительности в днях по плановым датам (включительно: начало и конец считаются)
 */
export const calcDurationFromDates = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = parseDateLocal(startDate);
  const end = parseDateLocal(endDate);
  if (!start || !end) return null;
  if (end < start) return null;
  const days = Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1;
  return Math.max(1, days);
};

/**
 * Быстрые даты
 */
export const getQuickDates = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);
  
  const nextMonth = new Date(today);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  
  const next3Months = new Date(today);
  next3Months.setMonth(next3Months.getMonth() + 3);
  
  // Конец текущего квартала
  const currentQuarter = Math.floor(today.getMonth() / 3);
  const endOfQuarter = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
  
  return {
    today: formatDateForInput(today),
    tomorrow: formatDateForInput(tomorrow),
    nextWeek: formatDateForInput(nextWeek),
    nextMonth: formatDateForInput(nextMonth),
    next3Months: formatDateForInput(next3Months),
    endOfQuarter: formatDateForInput(endOfQuarter)
  };
};

/**
 * Валидация дат (парсинг как локальные даты)
 */
export const validateDates = (startDate, endDate) => {
  if (!startDate || !endDate) return { valid: true, error: null };
  
  const start = parseDateLocal(startDate);
  const end = parseDateLocal(endDate);

  if (!start || !end) {
    return { valid: false, error: 'Некорректный формат даты' };
  }
  
  if (end < start) {
    return { valid: false, error: 'Дата окончания раньше даты начала' };
  }
  
  return { valid: true, error: null };
};

/**
 * Экспорт данных в CSV
 */
export const exportToCSV = (data, columns, filename = 'export.csv') => {
  // Заголовки
  const headers = columns.map(col => col.label).join(';');
  
  // Данные
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.field];
      
      // Форматирование дат
      if (col.type === 'date' && value) {
        value = formatDateDisplay(value);
      }
      
      // Форматирование чисел
      if (col.type === 'number' && value) {
        value = value.toString().replace('.', ',');
      }
      
      // Экранирование
      if (typeof value === 'string') {
        value = value.replace(/"/g, '""');
        if (value.includes(';') || value.includes('\n') || value.includes('"')) {
          value = `"${value}"`;
        }
      }
      
      return value ?? '';
    }).join(';');
  });
  
  // UTF-8 BOM для корректного отображения кириллицы в Excel
  const BOM = '\uFEFF';
  const csvContent = BOM + headers + '\n' + rows.join('\n');
  
  // Скачивание файла
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Экспорт данных в Excel
 */
export const exportToExcel = (data, columns, filename = 'export.xlsx', sheetName = 'Данные') => {
  // Подготавливаем данные для Excel
  const excelData = data.map(item => {
    const row = {};
    columns.forEach(col => {
      let value = item[col.field];
      
      // Форматирование дат
      if (col.type === 'date' && value) {
        value = formatDateDisplay(value);
      }
      
      // Форматирование чисел
      if (col.type === 'number' && value !== null && value !== undefined) {
        value = parseFloat(value);
      }
      
      row[col.label] = value ?? '';
    });
    return row;
  });

  // Создаём книгу и лист
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Устанавливаем ширину столбцов
  const colWidths = columns.map(col => {
    // Базовая ширина на основе заголовка
    let width = col.label.length + 2;
    
    // Проверяем данные для определения максимальной ширины
    data.forEach(item => {
      const value = item[col.field];
      if (value) {
        const strLen = String(value).length;
        if (strLen > width) width = strLen;
      }
    });
    
    // Ограничиваем максимальную ширину
    return { wch: Math.min(width, 50) };
  });
  worksheet['!cols'] = colWidths;

  // Добавляем лист в книгу
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Скачиваем файл
  XLSX.writeFile(workbook, filename);
};

/**
 * Подготовка строки для копирования
 */
export const prepareRowForCopy = (row, scheduleType) => {
  const copy = { ...row };
  
  // Удаляем идентификаторы и служебные поля
  delete copy.id;
  delete copy.created_at;
  delete copy.updated_at;
  delete copy.isNew;
  
  // Генерируем новый временный ID
  copy.id = `new-${Date.now()}`;
  copy.isNew = true;
  copy.schedule_type = scheduleType;
  
  // Очищаем фактические даты (копируем только план)
  copy.actual_start_date = null;
  copy.actual_end_date = null;
  
  return copy;
};

/**
 * Парсит строку YYYY-MM-DD как локальную дату (не UTC).
 * new Date("2025-03-15") даёт UTC полночь — смещение на день в части часовых поясов.
 */
export const parseDateLocal = (dateStr) => {
  if (!dateStr) return null;
  const iso = parseUserDateInput(dateStr);
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Добавление дней к дате. Парсит YYYY-MM-DD как локальную дату.
 */
export const addDaysToDate = (dateStr, days) => {
  if (!dateStr) return '';
  const date = parseDateLocal(dateStr);
  if (!date) return '';
  date.setDate(date.getDate() + days);
  return formatDateForInput(date);
};

